import { Images } from '../assets'
import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import PageTitle from '../components/PageTitle'
import ProjectCard from '../components/ProjectCard'
import Chip from '../components/Chip'
import ImpactStat from '../components/ImpactStat'
import NigeriaMap from '../components/NigeriaMap'
import AfricaMap from '../components/AfricaMap'








// Gallery images used to replace detail-page placeholders


const ph = (label) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400"><rect width="600" height="400" fill="#E6F0FF"/><text x="300" y="200" text-anchor="middle" dominant-baseline="middle" fill="#7E7E7E" font-size="18" font-family="sans-serif">${label}</text></svg>`
  )}`

const imageLookup = {
  '/projects/clientshot-fct': Images.ClientshotProjectCard,
  '/projects/clientshot-nasarawa': Images.Deployment4,
  '/projects/clientshot-apin-benue': Images.Apin01,
  '/projects/clientshot-nthrip': Images.NthripProjectCard,
  '/projects/cleft-surgery': Images.SmiletrainGallery3,
  '/projects/bridge': Images.BridgeGallery3,
  '/projects/opencrvs': ph('OpenCRVS'),
  '/projects/ekiti': Images.EkitiKeyFeatures,
  '/projects/kncv': Images.KncvGallery3,
  '/projects/talamus': Images.TalamusProject,
}

const STATIC_STYLES = {
  projectLogo: { width: 150 },
  clientLogo: { height: 50 },
}

const VideoCarousel = ({ videos }) => {
  const [index, setIndex] = useState(0)
  const [muted, setMuted] = useState(true)
  const videoRefs = useRef([])

  useEffect(() => {
    const video = videoRefs.current[index]
    if (video) {
      video.currentTime = 0
      const playPromise = video.play()
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {})
      }
    }
    return () => {
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    }
  }, [index])

  useEffect(() => {
    videoRefs.current.forEach((video) => {
      if (video) {
        video.muted = muted
      }
    })
  }, [muted])

  const handleEnded = () => {
    setIndex((prev) => (prev + 1) % videos.length)
  }

  const carouselTransform = useMemo(
    () => ({ transform: `translateX(-${index * 100}%)` }),
    [index]
  )

  return (
    <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 border border-[#E6E6E6]">
      <div
        className="flex h-full transition-transform duration-500 ease-out"
        style={carouselTransform}
      >
        {videos.map((src, i) => (
          <div key={i} className="w-full h-full flex-shrink-0 flex items-center justify-center">
            <video
              ref={(el) => { videoRefs.current[i] = el }}
              src={src}
              className="w-full h-full object-cover"
              muted={muted}
              playsInline
              onEnded={handleEnded}
              aria-label={`Video ${i + 1} of ${videos.length}`}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setMuted((prev) => !prev)}
        className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors"
        aria-label={muted ? 'Unmute videos' : 'Mute videos'}
      >
        {muted ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
          </svg>
        )}
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {videos.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            className={`w-2 h-2 rounded-full transition-colors ${i === index ? 'bg-white' : 'bg-white/50'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

const AutoplayVideo = ({ src }) => {
  const [muted, setMuted] = useState(true)
  const videoRef = useRef(null)

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.muted = muted
      const playPromise = video.play()
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {})
      }
    }
  }, [muted, src])

  return (
    <div className="relative w-full h-full">
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        muted={muted}
        playsInline
        autoPlay
        loop
        aria-label="Project demo video"
      />
      <button
        type="button"
        onClick={() => setMuted((prev) => !prev)}
        className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors"
        aria-label={muted ? 'Unmute video' : 'Mute video'}
      >
        {muted ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
          </svg>
        )}
      </button>
    </div>
  )
}

const projects = {
  opencrvs: {
    title: 'OpenCRVS',
    category: 'Civil Registration & Vital Statistics',
    partner: null,
    overview: `SeamHealth partnered with the Federal Ministry of Health to design, develop, and deploy a national civil registration and vital statistics platform. OpenCRVS digitizes birth and death registration processes, enabling real-time vital statistics generation and interoperability with national health information systems. The platform works offline, making registration possible at the community level even in remote areas with limited connectivity.`,
    problem: 'Fragmented paper-based civil registration systems leading to unregistered births, uncounted deaths, and lack of legal identity for vulnerable populations.',
    industry: 'Service delivery (accessibility), Information systems (data quality, analysis, dissemination, and use)',
    atAGlance: [
      { value: 'UNICEF', label: 'Primary client' },
      { value: 'National', label: 'Deployment scope' },
      { value: 'Multilingual', label: 'Platform capability' },
      { value: 'Real-time', label: 'Data synchronisation' },
    ],
    problemDetailed: {
      paragraphs: [
        `Nigeria's civil registration and vital statistics system suffered from low birth and death registration rates, leaving millions without legal identity documentation and limiting government visibility into population health data.`,
        `Without birth certificates, children face barriers to education, healthcare, and civic participation.`,
        `UNICEF and partners required a digital civil registration platform capable of operating in low-connectivity environments, supporting multiple languages, and integrating with government systems.`,
      ],
    },
    builtDeployed: {
      paragraphs: [
        `We contributed to the build and implementation of OpenCRVS, an open-source electronic civil registration and vital statistics system.`,
        `The platform enables registration of births, deaths, and other vital events through web and mobile interfaces. Registration data is validated, stored securely, synchronized in real time, and integrated with existing government databases.`,
        `We implemented multilingual support and offline-first functionality to ensure nationwide accessibility.`,
      ],
      highlights: ['Multilingual support', 'Offline data collection', 'Cloud synchronisation', 'Government database integration'],
    },
    keyFeaturesList: [
      'User-friendly web and mobile interfaces for birth, death, and vital event registration',
      'Real-time data synchronisation across platforms and administrative levels',
      'Secure, encrypted data storage and transmission',
      'Comprehensive analytics and reporting tools for national and state-level administrators',
      'Integration with existing government databases and systems',
      'Multilingual support for diverse user groups across Nigeria',
    ],
    confirmedOutcomes: [
      'National electronic civil registration system deployed',
      'Standardised digital alternative to paper-based registration',
      'Multilingual support for diverse populations',
      'National visibility into registration activity through real-time synchronisation',
    ],
    geography: 'Nigeria',
  },
  'clientshot-fct': {
    title: 'FCT Health Insurance Awareness and Enrollment Assessment',
    metaTitle: 'FCT Health Insurance Assessment | Clientshot | SeamHealth Innovations',
    metaDescription: `We deployed Clientshot across 99 health facilities in Abuja FCT, collecting 54,836 responses on health insurance awareness among people living with HIV over 19 months.`,
    logo: Images.ClientshotLogoWhite,
    heroImage: Images.Deployment6,
    clientLogo: Images.IhvnLogo,
    category: 'Information Systems',
    overview: `We deployed Clientshot across 99 health facilities in Abuja FCT, collecting 54,836 responses on health insurance awareness among people living with HIV over 19 months.`,
    problem: `People living with HIV in Nigeria's Federal Capital Territory faced significant gaps in health insurance awareness and enrollment. Despite federal and state-level efforts to expand coverage, programme managers lacked reliable, real-time data on how many PLHIV were aware of available schemes, why enrollment rates remained low, and which facilities were most affected.`,
    industry: `Information Systems`,
    atAGlance: [
      { value: '54,836', label: 'Valid responses collected' },
      { value: '99', label: 'Health facilities onboarded' },
      { value: '6', label: 'Area Councils covered' },
      { value: '100%+', label: 'Of projected target achieved' },
    ],
    problemImage: Images.Deployment3,
    problemDetailed: {
      paragraphs: [
        `People living with HIV in Nigeria's Federal Capital Territory faced significant gaps in health insurance awareness and enrollment. Despite federal and state-level efforts to expand coverage, programme managers lacked reliable, real-time data on how many PLHIV were aware of available schemes, why enrollment rates remained low, and which facilities were most affected.`,
        `IHVN, implementing a PEPFAR-funded programme across the FCT, needed a system that could collect high-volume beneficiary data across dozens of facilities simultaneously, synchronise it in real time, and surface actionable analytics for programme managers without requiring significant technical capacity at the facility level.`,
      ],
    },
    builtDeployed: {
      paragraphs: [
        `We deployed the Clientshot platform across 99 healthcare facilities spanning all six Area Councils of the FCT. The platform was configured to collect structured survey responses from PLHIV on health insurance awareness, current enrollment status, barriers to enrollment, scheme preferences, and willingness to contribute to costs.`,
        `Data was collected via both the Clientshot web application and mobile app, with responses synchronised in real time to a central analytics dashboard accessible to IHVN programme managers. Our team handled full deployment coordination including facility onboarding, staff orientation, and ongoing technical support throughout the 19-month data collection period.`,
      ],
      highlights: [],
      demoVideo: Images.ClientshotDeploymentVideo,
    },
    keyFeaturesList: [
      'High-volume multi-site data collection across 99 facilities',
      'Web and mobile data capture interfaces',
      'Real-time response synchronisation',
      'Centralised analytics dashboard',
      'Facility onboarding and deployment support',
      'Sustained long-term deployment over 19 months',
    ],
    confirmedOutcomes: [
      '54,836 valid responses collected, exceeding the programme target of 54,725',
      'Full geographic coverage achieved across all 6 Area Councils of the FCT',
      'Health insurance enrollment gaps identified and mapped by facility, enabling data-driven targeting of demand creation interventions',
      'Actionable reports generated for IHVN programme management, supporting evidence-based decision-making on health insurance outreach',
      'Strong system adoption demonstrated over 19 months of sustained deployment',
    ],
    geography: `Federal Capital Territory (FCT), Abuja.\n\nCoverage includes all six Area Councils:\n\n- Abuja Municipal\n- Bwari\n- Gwagwalada\n- Kuje\n- Kwali\n- Abaji`,
    geographyImage: Images.Deployment5,
    keyFeaturesImage: Images.Clientshot003,
    relatedProjects: [
      '/projects/clientshot-nasarawa',
      '/projects/clientshot-apin-benue',
      '/projects/clientshot-nthrip',
    ],
  },
  'clientshot-nasarawa': {
    title: 'Nasarawa State Health Insurance Awareness and Enrollment Assessment',
    logo: Images.ClientshotLogoWhite,
    heroImage: Images.Deployment4,
    clientLogo: Images.IhvnLogo,
    category: 'Health Insurance Awareness and Enrollment Assessment',
    overview: `We deployed Clientshot across 125 health facilities in Nasarawa State, collecting 65,514 responses on health insurance awareness among PLHIV across all 13 LGAs over 30 months.`,
    problem: `Health insurance enrollment among people living with HIV in Nasarawa State remained poorly understood. Programme managers needed state-level visibility into awareness levels, enrollment barriers, scheme preferences, and willingness to contribute to costs across all 13 LGAs.`,
    industry: `Information Systems`,
    atAGlance: [
      { value: '65,514', label: 'Valid responses collected' },
      { value: '125', label: 'Health facilities onboarded' },
      { value: '13', label: 'LGAs covered' },
      { value: '97.3%', label: 'Of projected target achieved' },
    ],
    problemDetailed: {
      paragraphs: [
        `Health insurance enrollment among people living with HIV in Nasarawa State remained poorly understood. Programme managers needed state-level visibility into awareness levels, enrollment barriers, scheme preferences, and willingness to contribute to costs across all 13 LGAs and across a large, geographically dispersed facility network.`,
        `Existing data collection methods could not sustain the volume, geographic coverage, or real-time reporting that IHVN's PEPFAR-funded programme required. A platform capable of operating across 125 facilities simultaneously, with centralised analytics and minimal facility-level technical burden, was needed.`,
      ],
    },
    builtDeployed: {
      paragraphs: [
        `We deployed the Clientshot platform across 125 healthcare facilities spanning all 13 Local Government Areas of Nasarawa State. The platform collected structured survey responses from PLHIV and other PEPFAR beneficiaries on health insurance awareness, enrollment status, barriers to uptake, scheme preferences, and willingness to enroll and contribute to cost.`,
        `Multi-site data synchronisation ensured that responses collected across the state's 125 facilities were available in real time to IHVN programme managers through a centralised analytics dashboard. The 30-month deployment duration demonstrated the platform's capacity for sustained, large-scale data collection over an extended programme period.`,
      ],
      highlights: [],
      demoVideo: Images.ClientshotProblemVideo3,
    },
    keyFeaturesList: [
      'High-volume multi-site data collection across 125 facilities',
      'Web and mobile data capture interfaces',
      'Real-time response synchronisation',
      'Centralised analytics dashboard',
      'Facility onboarding and deployment support',
      'Sustained long-term programme monitoring over 30 months',
    ],
    confirmedOutcomes: [
      '65,514 valid responses collected representing 97.3% of the 67,328 target',
      'Statewide coverage achieved across all 13 LGAs',
      'First statewide visibility into PLHIV health insurance enrollment patterns',
      'Key enrollment barriers identified to support targeted campaigns',
      'Sustained deployment over 30 months — the longest continuous Clientshot deployment on record',
    ],
    geography: `Nasarawa State, Nigeria — All 13 Local Government Areas.`,
    geographyImage: Images.Clientshot001,
    keyFeaturesImage: Images.Clientshot002,
    problemImage: Images.Clientshot003,
  },
  'clientshot-apin-benue': {
    title: 'Appointment Compliance and Continuous Quality Improvement – Benue State',
    logo: Images.ClientshotLogoWhite,
    heroImage: Images.Apin01,
    clientLogo: Images.IhvnLogo,
    category: 'Appointment Compliance and Continuous Quality Improvement',
    overview: `We deployed Clientshot across 26 health facilities in Benue State, capturing 22,570 patient feedback records on appointment compliance among PLHIV over eight months.`,
    problem: `Appointment compliance among people living with HIV is one of the most critical indicators of programme quality. Missed appointments lead to treatment gaps, viral load increases, and poorer health outcomes.`,
    industry: `Service Delivery | Information Systems`,
    atAGlance: [
      { value: '22,570', label: 'Respondent records captured' },
      { value: '41,996', label: 'Commendations recorded' },
      { value: '586', label: 'Complaints recorded' },
      { value: '26', label: 'Health facilities onboarded' },
    ],
    problemImage: Images.Deployment3,
    problemDetailed: {
      paragraphs: [
        `Appointment compliance among people living with HIV is one of the most critical indicators of programme quality. Missed appointments lead to treatment gaps, viral load increases, and poorer health outcomes. APIN required a systematic way to capture real-time patient feedback on appointment compliance and service experience across 26 healthcare facilities.`,
        `Without a structured feedback mechanism, service quality gaps remained invisible until they became clinical problems. The programme required a platform capable of collecting actionable patient feedback, aggregating insights centrally, and supporting continuous quality improvement in real time.`,
      ],
    },
    builtDeployed: {
      paragraphs: [
        `Clientshot was deployed across 26 healthcare facilities as APIN's primary CQI platform. The platform captures structured patient feedback on appointment compliance, service satisfaction, and healthcare experience through both the web application and mobile application.`,
        `Data synchronises in real time to a central analytics dashboard, enabling programme managers to monitor patient experience across all facilities. The deployment distinguishes between commendations and complaints to support targeted quality improvement interventions.`,
        `Mobile adoption steadily increased throughout deployment, growing from 35.9% to 62.4% by April 2026, demonstrating increasing confidence among field staff.`,
      ],
      highlights: [],
      demoImage: Images.Apin02,
    },
    keyFeaturesList: [
      'Real-time patient feedback capture on appointment compliance',
      'Web and mobile data collection interfaces',
      'Centralised analytics dashboard for programme managers',
      'Commendation and complaint tracking for targeted CQI',
      'Growing mobile adoption from 35.9% to 62.4%',
    ],
    confirmedOutcomes: [
      '22,570 respondent records captured',
      '41,996 commendations and 586 complaints recorded',
      'Mobile adoption increased from 35.9% to 62.4%',
      'Statewide visibility into patient experience established',
      'Real-time CQI insights supported targeted quality improvement',
    ],
    geography: `Benue State, Nigeria — 26 Healthcare Facilities.`,
    geographyImage: Images.Clientshot004,
    keyFeaturesImage: Images.Clientshot005,
  },
  'clientshot-nthrip': {
    title: 'IHVN Global Fund N-THRIP Continuous Quality Improvement Programme',
    logo: Images.ClientshotLogoWhite,
    heroImage: Images.NthripHeroNew,
    clientLogo: Images.IhvnLogo,
    category: 'Continuous Quality Improvement Programme',
    overview: `We deployed Clientshot as the primary CQI platform across HIV, TB and PrEP facilities in Gombe, Kwara, Anambra and Ebonyi States.`,
    problem: `The IHVN Global Fund N-THRIP project spans HIV, Tuberculosis and Long-Acting PrEP facilities across four Nigerian states. Managing continuous quality improvement across multiple disease programmes and facilities required a unified feedback and analytics platform capable of operating at institutional scale.`,
    industry: `Service Delivery | Information Systems`,
    atAGlance: [
      { value: '4', label: 'States covered' },
      { value: '3', label: 'Programme areas' },
      { value: '118', label: 'Healthcare facilities onboarded' },
      { value: '15,550', label: 'Respondent records captured' },
    ],
    problemImage: Images.NthripGeography,
    problemDetailed: {
      paragraphs: [
        `The IHVN Global Fund N-THRIP project spans HIV, Tuberculosis and Long-Acting PrEP facilities across four Nigerian states. Managing continuous quality improvement across multiple disease programmes and facilities required a unified feedback and analytics platform capable of operating at institutional scale.`,
        `Without a unified platform, quality improvement insights would remain siloed by state, facility and programme area, limiting programme managers' ability to identify trends and prioritise interventions.`,
      ],
    },
    builtDeployed: {
      demoImage: Images.NthripBuiltDeployed,
      paragraphs: [
        `Clientshot was deployed as the primary CQI platform across HIV, TB and LeN PrEP facilities in Gombe, Kwara, Anambra and Ebonyi States.`,
        `The platform captures patient satisfaction surveys, provider assessment instruments, treatment adherence indicators and medication access data while generating real-time dashboards for programme managers.`,
        `This represents the most complex Clientshot deployment to date, demonstrating the platform's ability to scale across multiple states, programmes and facility types within a single Global Fund programme.`,
      ],
      highlights: [],
    },
    keyFeaturesList: [
      'Multi-state, multi-programme CQI platform deployment',
      'Patient satisfaction surveys and provider assessment instruments',
      'Treatment adherence and medication access data capture',
      'Real-time cross-state analytics dashboards',
      'Scalable architecture for complex institutional environments',
    ],
    confirmedOutcomes: [
      'Clientshot established as the primary CQI platform for a Global Fund programme spanning four states',
      'Patient satisfaction, provider assessment and treatment adherence data consolidated into a unified platform',
      'Real-time cross-state analytics dashboards delivered',
      'Healthcare provider improvement supported through structured assessment',
      'Platform scalability demonstrated across a highly complex institutional environment',
    ],
    geography: `Four Nigerian States:\n\n- Gombe\n- Kwara\n- Anambra\n- Ebonyi`,
    geographyImage: Images.NthripGeography,
    keyFeaturesImage: Images.NthripHero,
  },
  'cleft-surgery': {
    title: 'Cleft and Surgery E-Registry',
    logo: Images.SmiletrainLogo,
    heroImage: Images.SmiletrainHeroNew,
    clientLogo: Images.SmiletrainLogo,
    category: 'Surgical Registry & Patient Tracking',
    overview: `We developed and deployed the Cleft and Surgery E-Registry, a web and mobile application serving as a centralized national database for cleft repair and other surgical procedures performed in Nigeria, operating across all six geopolitical zones.`,
    problem: `Nigeria had no centralized, standardized system for tracking patients with cleft and other birth defects across the country.`,
    problemImage: Images.SmiletrainGallery3,
    industry: `Surgical care (patient tracking), Information systems (registry, analytics)`,
    atAGlance: [
      { value: '3,055', label: 'Patients registered' },
      { value: '1,796', label: 'Patients discharged' },
      { value: '95%', label: 'Cleft and craniofacial cases' },
      { value: '6', label: 'Geopolitical zones covered' },
    ],
    problemDetailed: {
      paragraphs: [
        `Nigeria had no centralized, standardized system for tracking patients with cleft and other birth defects across the country. Surgical data was fragmented across individual facilities, making it impossible for SmileTrain, the Federal Ministry of Health, and clinical teams to monitor patient care pathways, track outcomes at a national level, or identify gaps in follow-up care.`,
        `Patients could move between facilities or fall out of care entirely without any record being updated. The absence of a unified registry meant that programme decisions - including resource allocation, surgical scheduling, and outcome reporting - were made without reliable national data.`,
      ],
    },
    builtDeployed: {
      paragraphs: [
        `We developed and deployed the Cleft and Surgery E-Registry, a web and mobile application serving as a centralized national database for cleft repair and other surgical procedures across Nigeria.`,
        `The platform captures comprehensive patient information across the full care continuum-from initial registration through pre-surgery assessment, surgery, post-surgery follow-up, and discharge. Each record includes family demographics, child and maternal data, birth details, diagnosis classification, and treatment outcomes.`,
        `The system operates across all six geopolitical zones and the FCT, with data entered at facility level and aggregated nationally in real time.`,
        `We built the platform as an independent registry governed jointly by SmileTrain Inc. and the Federal Ministry of Health.`,
      ],
      highlights: [],
      demoImage: Images.SmiletrainGallery1,
    },
    keyFeaturesList: [
      'Centralized web and mobile interfaces for data entry and access across all facilities',
      'Comprehensive patient records covering the full care pathway: registration, pre-surgery, surgery, post-surgery, and discharge',
      'Real-time analytics and reporting accessible to SmileTrain and the Federal Ministry of Health',
      'Secure, encrypted data storage and transmission',
      'Integration with existing health system workflows at the facility level',
    ],
    confirmedOutcomes: [
      '3,055 patients registered nationwide',
      '440 pre-surgery patients actively tracked',
      '380 surgery cases actively tracked',
      '428 post-surgery patients actively tracked',
      '1,796 patients discharged',
      '95% of registered conditions are cleft and craniofacial cases',
      'Real-time national visibility into surgical care pathways',
    ],
    geography: `Nationwide deployment across:\n\n* North Central\n* North East\n* North West\n* South East\n* South South\n* South West\n* Federal Capital Territory\n* Ghana\n* Ethiopia`,
    keyFeaturesImage: Images.SmiletrainGallery2,
  },
  bridge: {
    title: 'BRIDGE',
    logo: Images.BridgeLogoAlt,
    heroImage: Images.BridgeHeroNew,
    clientLogo: Images.BillGatesLogo,
    category: 'Breast Health Risk Assessment, Detection, and Guidance Engine',
    overview: `We developed BRIDGE, a mobile application designed for community health workers in low-resource, offline environments, guiding them through a structured, decision-tree-based breast health risk assessment process with clear referral pathways.`,
    problem: `Breast cancer is one of the leading causes of cancer death among women in Nigeria, yet early detection rates in rural areas remain critically low due to the lack of a structured assessment tool for community health workers.`,
    problemImage: Images.BridgeGallery1,
    industry: `Service delivery (screening, referral), Information systems (mobile health)`,
    atAGlance: [
      { value: 'CHW', label: 'Designed for community health workers' },
      { value: 'Offline', label: 'Full offline functionality' },
      { value: 'CHAI', label: 'Clinton Health Access Initiative' },
      { value: 'Gates', label: 'Bill and Melinda Gates Foundation' },
    ],
    problemDetailed: {
      paragraphs: [
        `Breast cancer is one of the leading causes of cancer death among women in Nigeria, yet early detection rates in rural areas remain critically low. Community health workers often lacked a structured, standardized tool to assess breast cancer risk and guide referral decisions.`,
        `Without a clear protocol, assessments were inconsistent, referral pathways were unclear, and opportunities for early detection were routinely missed.`,
        `CHAI and the Bill & Melinda Gates Foundation engaged SeamHealth to develop a mobile solution that could support evidence-based assessments without requiring internet connectivity.`,
      ],
    },
    keyFeaturesImage: Images.BridgeGallery3,
    builtDeployed: {
      paragraphs: [
        `We developed BRIDGE - Breast Health Risk Assessment, Detection, and Guidance Engine. The mobile application guides community health workers through a structured decision-tree assessment covering patient history, symptoms, and risk factors.`,
        `Based on assessment responses, BRIDGE generates a risk classification and referral recommendation.`,
        `The application was designed with full offline functionality, storing data locally and synchronizing once connectivity becomes available.`,
      ],
      demoImage: Images.BridgeGallery2,
      highlights: [],
    },
    keyFeaturesList: [
      'Structured, decision-tree-based assessment process for breast health risk evaluation',
      'Clear risk classification and referral pathways generated at the point of assessment',
      'Educational resources for CHWs on breast health and cancer warning signs',
      'Full offline functionality - data stored locally and synced when connectivity is available',
      'Secure data storage and management',
    ],
    confirmedOutcomes: [
      'Standardised breast health assessment process delivered to community health workers',
      'Clear referral pathways established at community level',
      'Offline functionality enabled use in remote environments',
      'Centralised analytics provided visibility into screening activity',
    ],
    geography: `Rural Nigeria.`,
    keyOfferings: [
      { title: 'Structured assessment process for breast health', image: Images.BridgeAssessmentIcon },
      { title: 'Decision trees to evaluate breast cancer risk', image: Images.BridgeTreeIcon },
      { title: 'Educational resources for CHWs', image: Images.EducationIcon },
      { title: 'Referral pathways for patients', image: Images.ReferralIcon },
      { title: 'Offline functionality for remote areas', image: Images.OfflineIcon },
      { title: 'Secure data storage and management', image: Images.BridgeSecureIcon },
    ],
  },
  ekiti: {
    title: 'Ekiti LIMS & Post-COVID Vaccination Tracking System',
    logo: Images.EkitiLogoPng,
    heroImage: Images.EkitiKeyFeatures,
    clientLogo: Images.IhvnLogo,
    category: 'Laboratory Information Management & Vaccination Tracking',
    overview: `We built a unified platform combining a Laboratory Information Management System for sample management and a post-COVID vaccination tracking system using USSD and WhatsApp chatbot technology for the Ekiti State Ministry of Health.`,
    problem: 'Ekiti State faced inefficient laboratory sample management and lacked a scalable way to monitor post-COVID vaccination outcomes.',
    problemImage: Images.EkitiProblem,
    keyFeaturesImage: Images.EkitiHero,
    industry: 'Service delivery (accessibility), Information systems (data quality, analysis, dissemination, and use)',
    atAGlance: [
      { value: 'Integrated', label: 'LIMS and Vaccination Tracker' },
      { value: 'USSD', label: 'Data collection channel' },
      { value: 'WhatsApp', label: 'Chatbot integration' },
      { value: 'Ekiti State', label: 'Ministry of Health' },
    ],
    problemDetailed: {
      paragraphs: [
        `Ekiti State faced inefficient laboratory sample management and lacked a scalable way to monitor post-COVID vaccination outcomes.`,
        `Manual laboratory workflows increased risk of sample loss while vaccination monitoring required channels accessible to citizens regardless of device type or internet access.`,
      ],
    },
    builtDeployed: {
      paragraphs: [
        `We built a unified platform combining a Laboratory Information Management System and a post-COVID vaccination tracking system.`,
        `The LIMS manages intake, processing, tracking, and results management across health facilities.`,
        `The vaccination tracking component uses USSD and WhatsApp chatbot technology to collect follow-up information, monitor adverse reactions, and support vaccination outreach efforts.`,
      ],
      highlights: [],
      demoImage: Images.EkitiBuiltDeployed,
    },
    keyFeaturesList: [
      'Integrated LIMS for sample intake, processing, and results management',
      'USSD-based vaccination follow-up and adverse reaction monitoring',
      'WhatsApp chatbot for patient engagement and data collection',
      'Real-time visibility into laboratory performance and vaccination coverage',
    ],
    confirmedOutcomes: [
      'Integrated LIMS and vaccination tracking platform delivered',
      'Standardised laboratory sample management across health facilities',
      'Vaccination follow-up enabled through USSD and WhatsApp',
      'Real-time visibility into laboratory performance and vaccination coverage',
    ],
    geography: 'Ekiti State, Nigeria',
  },
  smartest: {
    title: 'SMARTest',
    category: 'HIV Testing Services Platform',
    overview: `We developed SMARTest, a mobile and web-based HIV testing services platform supporting targeted testing, self-testing, and index testing with offline-first data collection across multiple states.`,
    problem: 'A significant proportion of people living with HIV in Nigeria do not know their status.',
    industry: 'Service delivery (accessibility), HIV testing services',
    atAGlance: [
      { value: '4', label: 'States covered' },
      { value: '95%', label: 'HIV status awareness programme target' },
      { value: 'Offline', label: 'Data collection capability' },
      { value: 'IHVN', label: 'Implementing partner' },
    ],
    problemDetailed: {
      paragraphs: [
        `A significant proportion of people living with HIV in Nigeria do not know their status.`,
        `IHVN required a technology solution capable of supporting HIV testing services across multiple states while operating reliably in varied connectivity environments.`,
      ],
    },
    builtDeployed: {
      paragraphs: [
        `We developed SMARTest, a mobile and web-based HIV testing services platform.`,
        `The platform supports targeted testing, self-testing, and index testing. Data is captured offline and synchronized once connectivity is available.`,
        `The solution tracks the full client journey from testing through counselling and linkage to ART care.`,
      ],
      highlights: [],
    },
    keyFeaturesList: [
      'Mobile and web-based HIV testing services platform',
      'Support for targeted testing, self-testing, and index testing',
      'Offline-first data collection with sync when connectivity available',
      'Full client journey tracking from testing through counselling to ART linkage',
    ],
    confirmedOutcomes: [
      'HIV testing platform deployed across four states',
      'Unified support for targeted testing, self-testing, and index testing',
      'Reliable offline-first data collection across varied connectivity environments',
      'Real-time programme visibility for IHVN',
    ],
    geography: 'Rivers State, Federal Capital Territory, Nasarawa State, Katsina State',
  },
  kncv: {
    title: 'KNCV TB Patient Retention System',
    logo: Images.KncvLogo2,
    heroImage: Images.KncvGallery6,
    clientLogo: Images.KncvLogoAlt,
    category: 'Tuberculosis Patient Retention and Engagement',
    overview: `We implemented a patient engagement and tracking system using USSD and SMS technologies to improve TB patient retention, automate appointment reminders, and provide real-time adherence monitoring across 14 TB LON regions.`,
    problem: 'KNCV Tuberculosis Foundation faced challenges with patient retention, increasing demand for services, and loss-to-follow-up tracking.',
    industry: 'Service delivery (accessibility), Patient retention',
    atAGlance: [
      { value: '14', label: 'TB LON regions' },
      { value: 'USSD', label: 'Patient engagement' },
      { value: 'SMS', label: 'Automated reminders' },
      { value: '2 States', label: 'Deployment coverage' },
    ],
    problemImage: Images.EhrWhatwedo3,
    problemDetailed: {
      paragraphs: [
        `KNCV Tuberculosis Foundation faced challenges with patient retention, increasing demand for services, and loss-to-follow-up tracking.`,
        `Patients undergoing TB treatment often require six months or more of continuous care, making retention essential.`,
      ],
    },
    builtDeployed: {
      paragraphs: [
        `We implemented a patient engagement and tracking system using USSD and SMS technologies.`,
        `The platform provides automated appointment reminders, provider notifications, patient engagement workflows, and adherence monitoring.`,
        `Programme managers receive real-time visibility into patient retention and engagement.`,
      ],
      highlights: [],
      demoImage: Images.KncvGallery5,
    },
    keyFeaturesImage: Images.KncvHero,
    keyFeaturesList: [
      'USSD-based patient engagement and tracking',
      'Automated SMS appointment reminders and provider notifications',
      'Patient retention and adherence monitoring',
      'Reduced provider workload through workflow automation',
    ],
    confirmedOutcomes: [
      'Increased demand for TB services',
      'Improved patient retention across treatment periods',
      'Better tracking of lost-to-follow-up patients',
      'Reduced provider workload through automation',
    ],
    geography: '14 TB LON regions across 2 states in Nigeria.',
  },
  talamus: {
    title: 'Talamus EHR Evaluation',
    heroImage: Images.TalamusHero,
    category: 'Electronic Health Record System Evaluation',
    overview: `We conducted a structured evaluation of the Talamus Electronic Health Record platform across 23 primary healthcare facilities in Lagos State, assessing data flow, platform utilization, and interoperability readiness.`,
    problem: 'Lagos State Primary Health Care Board required an evidence-based assessment of the Talamus EHR platform for suitability and interoperability readiness.',
    industry: 'Information systems (system evaluation, interoperability)',
    builtSectionTitle: 'What We Did',
    atAGlance: [
      { value: '23', label: 'PHC facilities assessed' },
      { value: 'Lagos State', label: 'Deployment scope' },
      { value: 'CHAI', label: 'Evaluation partner' },
      { value: 'Talamus EHR', label: 'Platform evaluated' },
    ],
    problemDetailed: {
      paragraphs: [
        `Lagos State Primary Health Care Board required an evidence-based assessment of the Talamus Electronic Health Record platform.`,
        `The objective was to determine platform suitability, interoperability readiness, and alignment with real-world PHC workflows.`,
      ],
    },
    keyFeaturesList: [
      'Comprehensive evaluation completed across 23 PHC facilities',
      'Data flow and platform utilization assessment',
      'Interoperability readiness analysis',
      'Stakeholder perspectives documented',
    ],
    confirmedOutcomes: [
      'Comprehensive evaluation completed across 23 facilities',
      'Key performance and interoperability gaps identified',
      'Stakeholder perspectives documented',
      'Gap analysis report delivered to support future digital health investments',
    ],
    geography: 'Lagos State, Nigeria. Field visits conducted across 23 primary healthcare facilities.',
    problemImage: Images.TalamusHeroNew,
    keyFeaturesImage: Images.TalamusMockup,
    builtDeployed: {
      paragraphs: [
        `We conducted a structured evaluation across 23 primary healthcare facilities.`,
        `Assessment areas included: data flow, platform utilization, interoperability requirements, data visualization capabilities, and alignment with digitization objectives.`,
        `The outcome was a comprehensive gap analysis report for LSPHCB and CHAI.`,
      ],
      highlights: [],
      demoImage: Images.TalamusWhatWeDid,
    },
  },
}

const slugList = Object.keys(projects)
const allProjectLinks = [
  ...slugList.map((s) => ({ link: `/projects/${s}`, title: projects[s].title, desc: projects[s].overview.slice(0, 120) + '...' })),
]

const pickRelated = (currentSlug, project) => {
  if (project.relatedProjects) {
    return project.relatedProjects
      .map((link) => allProjectLinks.find((p) => p.link === link))
      .filter(Boolean)
  }
  const pool = allProjectLinks.filter((p) => p.link !== `/projects/${currentSlug}`)
  const shuffled = [...pool].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, 3)
}

const ProjectShowcase = () => {
  const { slug } = useParams()
  const project = projects[slug]
  const related = useMemo(() => pickRelated(slug, project), [slug, project])
  const staticStyles = useMemo(() => STATIC_STYLES, [])

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFB]">
        <div className="text-center max-w-md px-8">
          <h1 className="text-5xl font-semibold text-[#2A2A2A]">Project Not Found</h1>
          <p className="text-[#5B5B5B] mt-4">The project you are looking for does not exist or may have been moved.</p>
          <Link to="/projects" className="btn btn-primary mt-8 inline-flex items-center gap-2">
            Back to Projects <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" loading="lazy" />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <PageTitle title={project.metaTitle || project.title + ' | SeamHealth Website'} description={project.metaDescription} />

      {/* Hero */}
      <section className="project-section relative flex flex-col justify-center !h-auto min-h-[78vh] md:min-h-[84vh] lg:min-h-[90vh] overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover -z-[1000]"
          autoPlay
          loop
          muted
          playsInline
          controls={false}
          disablePictureInPicture
        >
          <source src={Images.ProjectVid} type="video/mp4" />
        </video>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-8 md:px-12 pt-[200px] pb-16 md:pt-[200px] md:pb-20 lg:pt-[200px] lg:pb-24">
          <div className="max-w-[760px]">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-white/70">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">Home</Link>
                </li>
                <li className="text-white/40">/</li>
                <li>
                  <Link to="/projects" className="hover:text-white transition-colors">Projects</Link>
                </li>
                <li className="text-white/40">/</li>
                <li className="text-white truncate min-w-0 flex-1" aria-current="page">{project.title}</li>
              </ol>
            </nav>
            <h1 data-aos="fade-up" className="text-5xl font-bold text-white mb-6">{project.title}</h1>
            <p data-aos="fade-up" className="text-white/60 leading-relaxed">{project.overview}</p>
          </div>
        </div>
        <div data-aos="fade-up" className="relative w-full max-w-[1280px] mx-auto px-8 md:px-12 mb-[-3rem] md:mb-[-5rem] z-20">
          <div className="rounded-2xl overflow-hidden max-w-[760px] mx-auto shadow-xl rounded-b-2xl">
            <img
              src={project.heroImage}
              alt={project.title}
              className="w-full aspect-[16/9] object-cover scale-105 hover:scale-100 transition-transform duration-700"
              draggable="false"
              loading="lazy"
            />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 h-48 md:h-64">
          <svg
            viewBox="0 0 1440 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            className="w-full h-full block"
          >
            <path d="M0 200H1440V0L0 200Z" fill="white" />
          </svg>
        </div>
      </section>

      <section className='py-24 md:py-32 p-4 md:p-8'>
        <div className="max-w-[1000px] mx-auto">
          <h1 className="text-5xl text-gray500">Project Overview</h1>
          <p className="text-gray100 mt-6">{project.overview}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 mt-12 gap-12">
            <div className="mb-8 md:mb-0">
              <h5 className="heading-six text-4xl">Problem Solved</h5>
              <p className="text-gray100">{project.problem}</p>
            </div>
            <div>
              <h5 className="heading-six text-4xl">Healthcare Pillar Addressed</h5>
              <p className="text-gray100">{project.industry}</p>
            </div>
            <div>
              <h5 className="heading-six text-4xl">Client</h5>
              <p className="text-gray100"><img decoding="async" src={project.clientLogo || ph('Client Logo')} style={staticStyles.clientLogo} alt="Client logo" loading="lazy" /></p>
              <div>
                <Link to="/get-in-touch" className="btn btn-primary w-max mt-8">
                  Partner With Us <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" loading="lazy" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project At A Glance */}
      <section className='py-14 md:py-16 bg-[#F3F8FF] border-t border-[#E8F0FE]'>
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {project.atAGlance.map((item, i) => (
              <ImpactStat key={i} number={item.value} label={item.label} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className='py-24 md:py-32 p-4 md:p-8'>
        <div className="max-w-[1000px] mx-auto">
          <h1 className="text-5xl text-center mb-12">The Problem</h1>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {project.problemDetailed.paragraphs.map((p, i) => (
                <p key={i} className="text-gray100 leading-relaxed">{p}</p>
              ))}
            </div>
            {project.problemVideos && project.problemVideos.length > 0 ? (
              <VideoCarousel videos={project.problemVideos} />
            ) : (
              <div className="group relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 flex items-center justify-center text-gray-400 text-sm border border-[#E6E6E6]">
                <img decoding="async" src={project.problemImage || ph('The Problem - Project Screenshot')} alt="The problem" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What We Built and Deployed */}
      <section className='py-24 md:py-32 bg-[#F8FAFB] p-4 md:p-8'>
        <div className="max-w-[800px] mx-auto">
          <h1 className="text-5xl text-center mb-12">{project.builtSectionTitle || "What We Built and Deployed"}</h1>
          <div className="space-y-6">
            {project.builtDeployed.paragraphs.map((p, i) => (
              <p key={i} className="text-gray100 leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="group rounded-2xl overflow-hidden aspect-video bg-gray-100 flex items-center justify-center text-gray-400 text-sm border border-[#E6E6E6] mt-10 relative">
            {project.builtDeployed.demoVideo ? (
              <AutoplayVideo src={project.builtDeployed.demoVideo} />
            ) : project.builtDeployed.demoImage ? (
              <img decoding="async" src={project.builtDeployed.demoImage} alt="Project demo" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" loading="lazy" />
            ) : (
              <>
                <img decoding="async" src={ph('Project Demo Video')} alt="Project demo video placeholder" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:bg-white transition-colors cursor-pointer">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2A2A2A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mt-8 justify-center">
            {project.builtDeployed.highlights.map((h, i) => (
              <span key={i} className="inline-block text-sm px-5 py-2.5 rounded-full bg-white border border-[#E6E6E6] text-[#5B5B5B] shadow-sm">
                {h}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Confirmed Outcomes */}
      <section className='py-24 md:py-32 bg-[#F8FAFB] p-4 md:p-8'>
        <div className="max-w-[800px] mx-auto">
          <h1 className="text-5xl text-center mb-12">Confirmed Outcomes</h1>
          <ul className="space-y-6">
            {project.confirmedOutcomes.map((o, i) => (
              <li key={i} className="flex gap-4 text-gray100 leading-relaxed">
                <span className="w-6 h-6 rounded-full bg-[#007DD6]/10 text-[#007DD6] flex items-center justify-center shrink-0 mt-0.5 text-sm font-semibold">{i + 1}</span>
                {o}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Geography */}
      {project.geography && (
        <section className='py-24 md:py-32 p-4 md:p-8'>
          <div className="max-w-[1000px] mx-auto">
            <h1 className="text-5xl text-center mb-12">Geography</h1>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <p className="text-gray100 leading-relaxed whitespace-pre-line">{project.geography}</p>
              {slug === 'cleft-surgery' || slug === 'bridge' || slug === 'kncv' ? (
                <div className="rounded-2xl overflow-hidden aspect-square bg-white border border-[#E6E6E6]">
                  <AfricaMap className="w-full h-full" highlightCountries={['ng', 'gh', 'et']} />
                </div>
              ) : slug === 'clientshot-fct' || slug === 'ekiti' || slug === 'talamus' || slug === 'clientshot-nasarawa' || slug === 'clientshot-apin-benue' || slug === 'clientshot-nthrip' ? (
                <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-white border border-[#E6E6E6]">
                  <NigeriaMap
                    className="w-full h-full"
                    highlightStates={slug === 'clientshot-fct' ? ['fct'] : slug === 'ekiti' ? ['ekiti'] : slug === 'talamus' ? ['lagos'] : slug === 'clientshot-nasarawa' ? ['nassarawa'] : slug === 'clientshot-apin-benue' ? ['benue'] : slug === 'clientshot-nthrip' ? ['gombe', 'kwara', 'anambra', 'ebonyi'] : []}
                  />
                </div>
              ) : (
                <div className="group relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 flex items-center justify-center text-gray-400 text-sm border border-[#E6E6E6]">
                  <img decoding="async" src={project.geographyImage || ph('Geographic Coverage Map')} alt="Geographic coverage" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Key Features */}
      <section className='py-24 md:py-32 p-4 md:p-8'>
        <div className="max-w-[1000px] mx-auto">
          <h1 className="text-5xl text-center mb-12">Key Features</h1>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <ul className="space-y-4 list-disc marker:text-[#007DD6] text-gray100 leading-relaxed pl-5">
              {project.keyFeaturesList.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
            <div className="group relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 flex items-center justify-center text-gray-400 text-sm border border-[#E6E6E6]">
              <img decoding="async" src={project.keyFeaturesImage || ph('Key Features - Platform Screenshot')} alt="Key features" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
            </div>
          </div>
        </div>
      </section>

      

      {/* Deployment - IHVN GF NTHRIP (only Clientshot) */}
      {project.deployment && slug !== 'clientshot-fct' && (
        <div className='grid grid-cols-1 gap-8 p-4 md:p-8 mt-24'>
          <h6 className="heading-six opacity-70">Deployment</h6>
          <h1 className="text-5xl">IHVN GF NTHRIP Project</h1>
          <div>
            <p className=''>{project.deployment.desc}</p>
          </div>
          <div className="video-wrapper">
            <iframe width="100%" height="750px" loading="lazy" src="https://www.youtube.com/embed/kwJ5rVAT5sQ?si=MkbU4o5rwOTSAF4S" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
          </div>
        </div>
      )}

      {/* APIN - Strengthening Appointment Compliance (only Clientshot) */}
      {project.apin && slug !== 'clientshot-fct' && (
        <div className='grid grid-cols-1 gap-8 p-4 md:p-8 mt-24'>
          <h1 className="text-5xl">Strengthening Appointment Compliance through Client-Centered Digital Feedback in Benue State</h1>
          <div>
            <p className=''>{project.apin.desc}</p>
          </div>
          <div className='grid grid-cols-2'>
            <img decoding="async" src={project.apin.images[0]} className='opacity-50' alt="APIN project screenshot 1" loading="lazy" />
            <img decoding="async" src={project.apin.images[1]} className='opacity-50' alt="APIN project screenshot 2" loading="lazy" />
          </div>
        </div>
      )}

      <div className="recent-projects py-24 md:py-32 p-4 md:p-8">
        <div className="max-w-[1280px] mx-auto">
          <div className='flex flex-col md:flex-row justify-between items-start gap-4 md:gap-0 md:items-center'>
            <div className="flex gap-y-2 flex-col">
              <h1 className="text-5xl">Related Projects</h1>
            </div>
            <Link to="/projects" className="btn btn-primary">
              See all projects <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" loading="lazy" />
            </Link>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-3 my-24 gap-8'>
          {related
            .filter((r) => {
              const img = imageLookup[r.link]
              return img && !String(img).startsWith('data:image/svg+xml')
            })
            .map((r, i) => (
              <ProjectCard key={i} image={imageLookup[r.link]} title={r.title} desc={r.desc} link={r.link} tag={false} imageHeight="250px" />
            ))}
        </div>
        </div>
      </div>
    </>
  )
}

export default ProjectShowcase
